import { createApi, BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { AxiosRequestConfig, AxiosError } from "axios";
import type {
  Product,
  Category,
  Color,
  Size,
  Metadata,
  AnalyticsData,
  OrderListResponse,
} from "../types";
import { axiosInstance } from "./axios";

// Custom baseQuery using Axios for RTK Query
const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
    },
    unknown,
    unknown
  > =>
  async ({ url, method = "GET", data, params }) => {
    try {
      const result = await axiosInstance({
        url,
        method,
        data,
        params,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const api = createApi({
  reducerPath: "api",
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    "Product",
    "Order",
    "Customer",
    "Inventory",
    "Coupon",
    "Review",
    "Notification",
    "Profile",
  ],
  endpoints: (builder) => ({
    // Metadata (categories + colors + sizes)
    getMetadata: builder.query<{ data: Metadata }, void>({
      query: () => ({ url: "/metadata" }),
      providesTags: ["Product"],
    }),

    // Analytics
    getAnalytics: builder.query<{ data: AnalyticsData }, void>({
      query: () => ({ url: "/analytics/admin" }),
    }),

    // Products
    getProducts: builder.query<{ data: Product[] }, void>({
      query: () => ({ url: "/products" }),
      providesTags: ["Product"],
    }),
    getProduct: builder.query<{ data: Product }, string>({
      query: (id) => ({ url: `/products/${id}` }),
      providesTags: ["Product"],
    }),
    createProduct: builder.mutation<unknown, FormData>({
      query: (data) => ({ url: "/products/admin", method: "POST", data }),
      invalidatesTags: ["Product"],
    }),
    updateProduct: builder.mutation<
      unknown,
      { id: string; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/products/admin/${id}`,
        method: "PATCH",
        data: formData,
      }),
      invalidatesTags: ["Product"],
    }),
    deleteProduct: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/products/admin/${id}`, method: "DELETE" }),
      invalidatesTags: ["Product"],
    }),

    // Categories
    getCategories: builder.query<{ data: Category[] }, void>({
      query: () => ({ url: "/categories" }),
      providesTags: ["Product"],
    }),
    createCategory: builder.mutation<unknown, FormData>({
      query: (data) => ({ url: "/categories/admin", method: "POST", data }),
      invalidatesTags: ["Product"],
    }),
    updateCategory: builder.mutation<
      unknown,
      { id: string; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/categories/admin/${id}`,
        method: "PATCH",
        data: formData,
      }),
      invalidatesTags: ["Product"],
    }),
    deleteCategory: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/categories/admin/${id}`, method: "DELETE" }),
      invalidatesTags: ["Product"],
    }),

    // Colors
    getColors: builder.query<{ data: Color[] }, void>({
      query: () => ({ url: "/colors" }),
      providesTags: ["Product"],
    }),
    createColor: builder.mutation<unknown, Partial<Color>>({
      query: (data) => ({ url: "/colors/admin", method: "POST", data }),
      invalidatesTags: ["Product"],
    }),
    updateColor: builder.mutation<unknown, Partial<Color> & { id: string }>({
      query: ({ id, ...data }) => ({
        url: `/colors/admin/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["Product"],
    }),
    deleteColor: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/colors/admin/${id}`, method: "DELETE" }),
      invalidatesTags: ["Product"],
    }),

    // Sizes
    getSizes: builder.query<{ data: Size[] }, void>({
      query: () => ({ url: "/sizes" }),
      providesTags: ["Product"],
    }),
    createSize: builder.mutation<unknown, Partial<Size>>({
      query: (data) => ({ url: "/sizes/admin", method: "POST", data }),
      invalidatesTags: ["Product"],
    }),
    updateSize: builder.mutation<unknown, Partial<Size> & { id: string }>({
      query: ({ id, ...data }) => ({
        url: `/sizes/admin/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["Product"],
    }),
    deleteSize: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/sizes/admin/${id}`, method: "DELETE" }),
      invalidatesTags: ["Product"],
    }),

    // Orders
    getOrders: builder.query<
      OrderListResponse,
      { page?: number; limit?: number }
    >({
      query: ({ page = 1, limit = 10 }) => ({
        url: "/admin/orders",
        params: { page, limit },
      }),
      providesTags: ["Order"],
    }),
    updateOrderStatus: builder.mutation<
      { message: string },
      { id: string; status?: string; paymentStatus?: string }
    >({
      query: ({ id, ...data }) => ({
        url: `/admin/orders/${id}`,
        method: "PUT",
        data,
      }),
      invalidatesTags: ["Order"],
    }),
  }),
});

export const {
  useGetMetadataQuery,
  useGetAnalyticsQuery,
  useGetProductsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetColorsQuery,
  useCreateColorMutation,
  useUpdateColorMutation,
  useDeleteColorMutation,
  useGetSizesQuery,
  useCreateSizeMutation,
  useUpdateSizeMutation,
  useDeleteSizeMutation,
  useGetOrdersQuery,
  useUpdateOrderStatusMutation,
} = api;
